import { FC } from "react"
import { Provider } from "react-redux"
import { BrowserRouter } from "react-router-dom"
import { ToastContainer } from "react-toastify"
import { useAppInit } from "@/hooks/useAppInit"
import { useEncumbrance } from "@/hooks/useEncumbrance"
import { useTokenRefresh } from "@/hooks/useTokenRefresh"
import { Modals, ModalsProvider } from "@/modals"
import { ErrorBoundary } from "@/providers/ErrorBoundary"
import { HeroVitalsProvider } from "@/providers/HeroVitals"
import { WebAppViewportProvider } from "@/providers/WebAppViewport"
import { WithGsap } from "@/providers/WithGsap"
import { ConnectionOverlay } from "@/ui/ConnectionOverlay"
import { NavigationHud } from "@/ui/NavigationHud"
import { Router } from "./pages"
import { store } from "./store"

// src/ui/* must not read the RTK Query cache — this composition-root
// container owns the inventory subscription and feeds the HUD a prop.
const NavigationHudContainer: FC = () => {
  const encumbrance = useEncumbrance()

  return <NavigationHud encumbrance={encumbrance} />
}

const AppShell: FC = () => {
  const { isInit } = useAppInit()

  useTokenRefresh()

  if (!isInit) {
    return null
  }

  return (
    <WithGsap>
      <Provider store={store}>
        <ModalsProvider>
          <ErrorBoundary>
            <WebAppViewportProvider>
              <HeroVitalsProvider>
                <Router />
                <Modals />
                <NavigationHudContainer />
                <ConnectionOverlay />
                <ToastContainer
                  position="bottom-right"
                  autoClose={3000}
                  closeOnClick
                  pauseOnHover
                  theme="dark"
                />
              </HeroVitalsProvider>
            </WebAppViewportProvider>
          </ErrorBoundary>
        </ModalsProvider>
      </Provider>
    </WithGsap>
  )
}

export const App: FC = () => (
  <BrowserRouter>
    <AppShell />
  </BrowserRouter>
)
